import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import './ShiftlineTemplateSelection.scss';
import {
    AppBar,
    Chip,
    Grid,
    Button,
    Divider,
    IconButton,
    Dialog,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { WarningTwoTone } from '@material-ui/icons';
import {
    ShiftlineSelectionModals, ShiftlineSelectionTableType, AnchorDay, ScheduleSource, PeriodType,
} from '../../Enums';
import { saveBidSelectionTemplate } from '../store/bidSelectionTemplate';
import { RESET_BID_SELECTION, handleAutoOrderSelected } from '../../store/bidSelection';
import ShiftlineFilterSelectionBar from '../components/ShiftlineFilterSelectionBar';
import ShiftLineSelectionTable from '../components/ShiftLineSelectionTable/ShiftLineSelectionTable';
import ShiftLineSelectionMobile from '../components/ShiftLineSelectionMobile';
import { ShiftlineSelectionConfirmation } from '../../components';
import { DAY_COLUMNS_COUNT } from '../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';

function ShiftlineTemplateSelection() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee' });

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidSelectionTemplate, employeeBidSelection, employeeBidProfile } = useSelector((state) => state);
    const { isGroupShiftlinesWithSameSchedules } = employeeBidProfile;

    const [windowWidth, setWindowWidth] = useState(0);
    const [showModal, setShowModal] = useState(ShiftlineSelectionModals.None);
    const [autoNumberingType, setAutoNumberingType] = useState('');

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSaveTemplate = () => {
        dispatch(saveBidSelectionTemplate(employeeBidSelectionTemplate.sourceProfileId, employeeBidSelection.employeeSelections));
        setShowModal(ShiftlineSelectionModals.None);
    };

    const handleAutoOrder = () => {
        dispatch(
            handleAutoOrderSelected(
                employeeBidSelection,
                autoNumberingType,
                employeeBidSelection.filteredItems,
                isGroupShiftlinesWithSameSchedules
            )
        );
        setShowModal(ShiftlineSelectionModals.None);
    };

    const handleBack = () => {
        dispatch(push('/Employee'));
        dispatch({ type: RESET_BID_SELECTION });
    };

    const isMobile = windowWidth < 992;
    return (
        <>
            {
                isMobile ? (
                    <div className="shifts-list-container">
                        <div className="topMenuBar">
                            <div className="slt-topMenuFirstRow">
                                <div className="slt-header-top">
                                    <IconButton onClick={() => handleBack()}>
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                    </IconButton>
                                    <p className="headline-h-700 slt-bid-name">{employeeBidSelectionTemplate.name}</p>
                                </div>
                                <div className="slt-filterSelectionTab slt-header-bottom">
                                    <ShiftlineFilterSelectionBar />
                                </div>
                            </div>
                        </div>
                        <ShiftLineSelectionMobile
                            isMultipleSelection
                            daysPerPeriod={DAY_COLUMNS_COUNT}
                        />
                        <AppBar
                            position="fixed"
                            className="slt-fixed-buttons-container"
                        >
                            <div
                                className="slt-buttons-container"
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowModal(ShiftlineSelectionModals.AutoNumbering)}
                                >
                                    {t('common.selectInOrder')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(ShiftlineSelectionModals.SaveTemplate)}
                                    disabled={employeeBidSelection.employeeSelections.length === 0}
                                >
                                    {t('common.saveTemplate')}
                                </button>
                            </div>
                        </AppBar>
                    </div>
                ) : (
                    <>
                        <Grid
                            container
                            className="slt-selection-topbar"
                        >
                            <Grid
                                item
                                xs={8}
                                lg={8}
                                className="header-properties-container"
                            >
                                <Grid
                                    container
                                    className="header-properties-line-container"
                                >
                                    <Grid
                                        item
                                    >
                                        <Grid
                                            container
                                        >
                                            <Grid
                                                item
                                                lg={3}
                                                className="header-title-container"
                                            >
                                                <IconButton onClick={() => handleBack()}>
                                                    <FontAwesomeIcon icon={faArrowLeft} />
                                                </IconButton>
                                                <p className="headline-h-700 slt-bid-name">{employeeBidSelectionTemplate.name}</p>
                                            </Grid>
                                            <Grid
                                                item
                                                lg={3}
                                                className="header-status-container"
                                            >
                                                <Chip
                                                    label={t('dashboard.tabs.template')}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                lg={4}
                            >
                                <Grid
                                    container
                                    spacing={0}
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    className="buttons-container"
                                >
                                    <Grid
                                        item
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setShowModal(ShiftlineSelectionModals.SaveTemplate)}
                                        >
                                            {t('common.saveTemplate')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid
                            container
                            className="slt-selection-filters"
                        >
                            <ShiftlineFilterSelectionBar />
                        </Grid>
                        {
                            employeeBidSelectionTemplate.items.length ? (
                                <ShiftLineSelectionTable
                                    isMultipleSelection
                                    daysPerPeriod={DAY_COLUMNS_COUNT}
                                    type={ShiftlineSelectionTableType.EditSelection}
                                    externalStoreData={{
                                        filteredItems: [],
                                        shiftsDictionary: {},
                                        anchorDay: AnchorDay.Sunday,
                                        scheduleSource: ScheduleSource.ExcelFile,
                                        dayCount: 0,
                                        periodType: PeriodType.Weeks,
                                    }}
                                />
                            ) : (
                                <div className="noAvailableScheduleWarningBox">
                                    <WarningTwoTone
                                        className="noAvailableScheduleWarningIcon"
                                        color="error"
                                    />
                                    <span
                                        className="noAvailableScheduleWarningText"
                                    >
                                        {t('dashboard.noneAvailable')}
                                    </span>
                                </div>
                            )
                        }
                    </>
                )
            }
            <Dialog
                open={showModal !== ShiftlineSelectionModals.None}
                onClose={() => setShowModal(ShiftlineSelectionModals.None)}
            >
                <ShiftlineSelectionConfirmation
                    showModal={showModal}
                    templateToLoadName=""
                    autoNumberingType={autoNumberingType}
                    setShowModal={setShowModal}
                    handleSaveTemplate={handleSaveTemplate}
                    setAutoNumberingType={setAutoNumberingType}
                    handleAutoOrder={handleAutoOrder}
                    t={t}
                />
            </Dialog>
        </>
    );
}

export default ShiftlineTemplateSelection;
